import axios from 'axios'

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://127.0.0.1:3333/api',
  timeout: 10000
})

let authToken = window.localStorage.getItem('demandas_token') || ''

api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`
  }
  return config
})

export default {
  setToken (token) {
    authToken = token
    window.localStorage.setItem('demandas_token', token)
  },
  clearToken () {
    authToken = ''
    window.localStorage.removeItem('demandas_token')
  },
  hasToken () {
    return Boolean(authToken)
  },
  getToken () {
    return authToken
  },
  login (payload) {
    return api.post('/auth/login', payload).then((response) => response.data)
  },
  forgotPassword (payload) {
    return api.post('/auth/forgot-password', payload).then((response) => response.data)
  },
  resetPassword (payload) {
    return api.post('/auth/reset-password', payload).then((response) => response.data)
  },
  changePassword (payload) {
    return api.put('/auth/change-password', payload).then((response) => response.data)
  },
  getClientInvite (token) {
    return api.get(`/client-invites/${token}`).then((response) => response.data)
  },
  registerClientInvite (token, payload) {
    return api.post(`/client-invites/${token}/register`, payload).then((response) => response.data)
  },
  me () {
    return api.get('/auth/me').then((response) => response.data)
  },
  listClientInvites () {
    return api.get('/client-invites').then((response) => response.data)
  },
  createClientInvite (payload) {
    return api.post('/client-invites', payload).then((response) => response.data)
  },
  deleteClientInvite (id) {
    return api.delete(`/client-invites/${id}`).then((response) => response.data)
  },
  listRequests (params) {
    return api.get('/requests', { params }).then((response) => response.data)
  },
  createRequest (payload) {
    return api.post('/requests', payload).then((response) => response.data)
  },
  getRequest (id) {
    return api.get(`/requests/${id}`).then((response) => response.data)
  },
  updateRequest (id, payload) {
    return api.put(`/requests/${id}`, payload).then((response) => response.data)
  },
  archiveRequest (id) {
    return api.post(`/requests/${id}/archive`).then(response => response.data)
  },
  restoreRequest (id) {
    return api.post(`/requests/${id}/restore`).then(response => response.data)
  },
  deleteRequest (id) {
    return api.delete(`/requests/${id}`).then(response => response.data)
  },
  listRequestMessages (id) {
    return api.get(`/requests/${id}/messages`).then((response) => response.data)
  },
  createRequestMessage (id, payload) {
    const body = typeof payload === 'string' ? { text: payload } : payload
    return api.post(`/requests/${id}/messages`, body).then((response) => response.data)
  },
  linkRequestDemand (id, demandId) {
    return api.post(`/requests/${id}/demands`, { demandId }).then((response) => response.data)
  },
  unlinkRequestDemand (id, demandId) {
    return api.delete(`/requests/${id}/demands/${demandId}`).then((response) => response.data)
  },
  listDemands (params) {
    return api.get('/demands', { params }).then((response) => response.data)
  },
  getDemand (id) {
    return api.get(`/demands/${id}`).then((response) => response.data)
  },
  createDemand (payload) {
    return api.post('/demands', payload).then((response) => response.data)
  },
  updateDemand (id, payload) {
    return api.put(`/demands/${id}`, payload).then((response) => response.data)
  },
  setDemandArchived (id, archived) {
    return api.put(`/demands/${id}/archive`, { archived }).then((response) => response.data)
  },
  deleteDemand (id) {
    return api.delete(`/demands/${id}`)
  },
  addNote (id, text, mentions = []) {
    return api.post(`/demands/${id}/notes`, { text, mentions }).then((response) => response.data)
  },
  listProjects () {
    return api.get('/projects').then((response) => response.data)
  },
  createProject (payload) {
    return api.post('/projects', payload).then((response) => response.data)
  },
  listProjectLists (projectId) {
    return api.get(`/projects/${projectId}/lists`).then((response) => response.data)
  },
  listProjectClients (projectId) {
    return api.get(`/projects/${projectId}/clients`).then((response) => response.data)
  },
  createProjectList (projectId, payload) {
    return api.post(`/projects/${projectId}/lists`, payload).then((response) => response.data)
  },
  reorderProjectLists (projectId, names) {
    return api.put(`/projects/${projectId}/lists/order`, { names }).then((response) => response.data)
  },
  getProjectSchedule (projectId) {
    return api.get(`/projects/${projectId}/schedule`).then((response) => response.data)
  },
  createScheduleModule (projectId, payload) {
    return api.post(`/projects/${projectId}/schedule/modules`, payload).then((response) => response.data)
  },
  updateScheduleModule (id, payload) {
    return api.put(`/schedule/modules/${id}`, payload).then((response) => response.data)
  },
  deleteScheduleModule (id) {
    return api.delete(`/schedule/modules/${id}`)
  },
  createScheduleStory (moduleId, payload) {
    return api.post(`/schedule/modules/${moduleId}/stories`, payload).then((response) => response.data)
  },
  updateScheduleStory (id, payload) {
    return api.put(`/schedule/stories/${id}`, payload).then((response) => response.data)
  },
  deleteScheduleStory (id) {
    return api.delete(`/schedule/stories/${id}`)
  },
  listWhatsappGroups () {
    return api.get('/whatsapp/groups').then((response) => response.data)
  },
  syncWhatsappGroups () {
    return api.post('/whatsapp/groups/sync', {}, { timeout: 20000 }).then((response) => response.data)
  },
  updateWhatsappGroup (groupJid, payload) {
    return api.put(`/whatsapp/groups/${encodeURIComponent(groupJid)}`, payload).then((response) => response.data)
  },
  deleteWhatsappGroup (groupJid) {
    return api.delete(`/whatsapp/groups/${encodeURIComponent(groupJid)}`)
  },
  listUsers () {
    return api.get('/users').then((response) => response.data)
  },
  createUser (payload) {
    return api.post('/users', payload).then((response) => response.data)
  },
  updateUser (id, payload) {
    return api.put(`/users/${id}`, payload).then((response) => response.data)
  },
  updateUserProjects (id, projectIds) {
    return api.put(`/users/${id}/projects`, { projectIds }).then((response) => response.data)
  },
  projectMonthlyReport (params) {
    return api.get('/reports/project-monthly', { params }).then((response) => response.data)
  },
  hourMonthlyReport (params) {
    return api.get('/reports/hour-monthly', { params }).then((response) => response.data)
  },
  summary () {
    return api.get('/demands/summary').then((response) => response.data)
  },
  listDemandDrafts () {
    return api.get('/demand-drafts').then((response) => response.data)
  },
  createDemandDraft (payload) {
    return api.post('/demand-drafts', payload).then((response) => response.data)
  },
  updateDemandDraft (id, payload) {
    return api.put(`/demand-drafts/${id}`, payload).then((response) => response.data)
  },
  deleteDemandDraft (id) {
    return api.delete(`/demand-drafts/${id}`)
  },
}
